import { motion } from "framer-motion";
import { Link } from "react-router-dom";
import { Leaf, ArrowRight, Recycle } from "lucide-react";

/**
 * GeraeteretterPraemie — Nachhaltigkeits-Promo
 * Zeigt das echte "Geräteretter Prämie" Logo prominent mit
 * Beschreibung warum Reparieren statt wegwerfen sinnvoll ist.
 * Eingebettet zwischen Konfigurator und Featured Products.
 */
export default function GeraeteretterPraemie() {
  return (
    <section className="relative py-20 md:py-28 bg-[#0A0A0B] overflow-hidden" data-testid="geraeteretter-section">
      {/* Subtle green tint from the rainbow gradient */}
      <div className="absolute inset-0 bg-gradient-to-r from-transparent via-emerald-500/[0.03] to-transparent pointer-events-none"></div>

      <div className="max-w-7xl mx-auto px-6 lg:px-12 relative">
        <motion.div
          initial={{ opacity: 0, y: 30 }}
          whileInView={{ opacity: 1, y: 0 }}
          viewport={{ once: true }}
          transition={{ duration: 0.7, ease: [0.22, 1, 0.36, 1] }}
          className="relative rounded-[32px] border border-white/8 bg-gradient-to-br from-[#0F0F12] via-[#101216] to-[#0A0A0B] overflow-hidden"
        >
          {/* Rainbow accent line top */}
          <div className="absolute top-0 left-0 right-0 h-[3px] bg-gradient-to-r from-red-500 via-orange-400 via-yellow-400 via-lime-400 to-emerald-500"></div>

          {/* Glow */}
          <div className="absolute -top-20 -right-20 w-72 h-72 rounded-full bg-emerald-500/10 blur-3xl pointer-events-none"></div>
          <div className="absolute -bottom-20 -left-20 w-72 h-72 rounded-full bg-amber-500/10 blur-3xl pointer-events-none"></div>

          <div className="relative grid md:grid-cols-12 gap-10 lg:gap-14 items-center p-8 md:p-12 lg:p-16">

            {/* Left: Geräteretter logo */}
            <div className="md:col-span-5 flex justify-center md:justify-start">
              <div className="relative">
                <div className="absolute -inset-8 bg-gradient-to-br from-emerald-500/20 to-amber-500/15 blur-2xl rounded-full"></div>
                <div className="relative">
                  <img
                    src="/images/store/geraeteretter-praemie.png"
                    alt="Geräteretter Prämie — Reparieren statt Wegwerfen"
                    className="block w-[220px] md:w-[300px] h-auto drop-shadow-[0_4px_24px_rgba(255,255,255,0.08)]"
                    data-testid="geraeteretter-logo"
                  />
                </div>
                {/* Floating eco badge */}
                <div className="absolute -bottom-3 -right-3 md:-bottom-2 md:-right-2 flex items-center gap-1.5 px-3 py-1.5 rounded-full bg-emerald-500 text-black text-[11px] font-bold uppercase tracking-wide shadow-xl">
                  <Leaf className="w-3 h-3" /> Nachhaltig
                </div>
              </div>
            </div>

            {/* Right: Text + CTA */}
            <div className="md:col-span-7">
              <div className="inline-flex items-center gap-2 px-3 py-1.5 rounded-full bg-emerald-500/10 border border-emerald-500/30 text-[10px] uppercase tracking-[0.22em] text-emerald-300 font-bold">
                <Recycle className="w-3 h-3" /> Reparieren statt Wegwerfen
              </div>

              <h2 className="font-display text-3xl md:text-5xl font-bold tracking-tight text-white mt-5 leading-[1.05]">
                Bis zu <span className="bg-gradient-to-r from-amber-400 to-emerald-400 bg-clip-text text-transparent">130&nbsp;€</span><br/>
                Geräteretter&nbsp;Prämie.
              </h2>

              <p className="mt-5 text-base md:text-lg text-zinc-400 leading-relaxed max-w-xl">
                Das österreichische Reparatur-Förderprogramm zahlt bis zu <span className="text-white font-medium">50% der Reparaturkosten</span> zurück — bei uns einfach mit deinem Auftrag einreichen.
                Gut für deine Geldbörse. Gut fürs Klima.
              </p>

              <div className="mt-6 grid grid-cols-3 gap-3 max-w-md">
                <Stat n="50%" l="Zuschuss" color="text-emerald-400" />
                <Stat n="130€" l="Max. Bonus" color="text-amber-400" />
                <Stat n="100%" l="Bei uns" color="text-[#FF2D40]" />
              </div>

              <div className="mt-7 flex flex-col sm:flex-row gap-3">
                <Link to="/reparaturen#configurator" data-testid="geraeteretter-cta">
                  <button className="inline-flex items-center justify-center gap-1.5 rounded-full bg-white text-[#0A0A0B] h-12 px-7 font-medium btn-lift hover:bg-zinc-100 transition-colors">
                    Reparatur starten <ArrowRight className="w-4 h-4" />
                  </button>
                </Link>
                <a
                  href="https://www.reparaturbonus.at"
                  target="_blank"
                  rel="noreferrer"
                  className="inline-flex items-center justify-center gap-1.5 rounded-full bg-transparent border border-white/15 h-12 px-7 text-white font-medium hover:bg-white/5 transition-colors"
                  data-testid="geraeteretter-info-link"
                >
                  Mehr über die Prämie
                </a>
              </div>
            </div>
          </div>
        </motion.div>
      </div>
    </section>
  );
}

function Stat({ n, l, color }) {
  return (
    <div className="border-l border-white/10 pl-3">
      <div className={`font-display font-bold text-2xl md:text-3xl numeric ${color}`}>{n}</div>
      <div className="text-[10px] uppercase tracking-[0.18em] text-zinc-500 mt-1">{l}</div>
    </div>
  );
}
